// src/app/core/services/settings.service.ts
import { effect, inject, Injectable, signal } from '@angular/core';
import { StorageService } from './storage.service';

export type ColorScheme = 'default' | 'blue' | 'teal' | 'orange';

export interface AppSettings {
  readonly darkMode: boolean;
  readonly colorScheme: ColorScheme;
  readonly titleSize: number;
  readonly textSize: number;
  readonly fontSize: number;
}

export const DEFAULT_SETTINGS: AppSettings = {
  darkMode: false,
  colorScheme: 'default',
  titleSize: 50,
  textSize: 50,
  fontSize: 50,
};

const STORAGE_KEY = 'digimep.settings';

// Map slider value [0, 100] to a typography scale multiplier.
// 0 → 0.85, 50 → 1.00, 100 → 1.15.
function toScale(value: number): number {
  const clamped = Math.max(0, Math.min(100, value));
  return 0.85 + (clamped / 100) * 0.3;
}

@Injectable({ providedIn: 'root' })
export class SettingsService {
  private readonly storage = inject(StorageService);

  readonly darkMode = signal(DEFAULT_SETTINGS.darkMode);
  readonly colorScheme = signal<ColorScheme>(DEFAULT_SETTINGS.colorScheme);
  readonly titleSize = signal(DEFAULT_SETTINGS.titleSize);
  readonly textSize = signal(DEFAULT_SETTINGS.textSize);
  readonly fontSize = signal(DEFAULT_SETTINGS.fontSize);

  constructor() {
    this.hydrateFromStorage();

    this.applyToDom();

    effect(() => {
      this.applyToDom();
      this.persist();
    });
  }

  setDarkMode(value: boolean): void {
    this.darkMode.set(value);
  }

  setColorScheme(scheme: ColorScheme): void {
    this.colorScheme.set(scheme);
  }

  setTitleSize(value: number): void {
    this.titleSize.set(value);
  }

  setTextSize(value: number): void {
    this.textSize.set(value);
  }

  setFontSize(value: number): void {
    this.fontSize.set(value);
  }

  reset(): void {
    this.darkMode.set(DEFAULT_SETTINGS.darkMode);
    this.colorScheme.set(DEFAULT_SETTINGS.colorScheme);
    this.titleSize.set(DEFAULT_SETTINGS.titleSize);
    this.textSize.set(DEFAULT_SETTINGS.textSize);
    this.fontSize.set(DEFAULT_SETTINGS.fontSize);
  }

  private hydrateFromStorage(): void {
    const stored = this.storage.get<Partial<AppSettings>>(STORAGE_KEY);
    if (!stored) {
      return;
    }
    if (typeof stored.darkMode === 'boolean') this.darkMode.set(stored.darkMode);
    if (stored.colorScheme) this.colorScheme.set(stored.colorScheme);
    if (typeof stored.titleSize === 'number') this.titleSize.set(stored.titleSize);
    if (typeof stored.textSize === 'number') this.textSize.set(stored.textSize);
    if (typeof stored.fontSize === 'number') this.fontSize.set(stored.fontSize);
  }

  private applyToDom(): void {
    if (typeof document === 'undefined') {
      return;
    }
    const root = document.documentElement;
    root.setAttribute('data-theme', this.darkMode() ? 'dark' : 'light');
    root.setAttribute('data-color-scheme', this.colorScheme());
    root.style.setProperty('--scale-title', String(toScale(this.titleSize())));
    root.style.setProperty('--scale-text', String(toScale(this.textSize())));
    root.style.setProperty('--scale-body', String(toScale(this.fontSize())));
  }

  private persist(): void {
    const snapshot: AppSettings = {
      darkMode: this.darkMode(),
      colorScheme: this.colorScheme(),
      titleSize: this.titleSize(),
      textSize: this.textSize(),
      fontSize: this.fontSize(),
    };
    this.storage.set(STORAGE_KEY, snapshot);
  }
}
